/**
 * AdminConversionFeedbackTab
 * Admin dashboard view of demo-to-pro conversion feedback submissions.
 *
 * V2 (2026-04-27): adds PMF score column, upgrade-intent + contact-requested
 * filters, and a structured DetailPanel that groups V2 fields (PMF / Yes-path
 * / No-path / Contact) and falls back to V1 fields for legacy rows.
 */
import { h } from 'preact';
import { useState, useEffect, useMemo } from 'preact/hooks';
import { formatDateTime } from '../../utils/dateUtils.js';
import { getConversionFeedback, getConversionFeedbackCsvUrl } from '../../services/adminService.js';

const REASON_LABELS = {
  budget: 'Budget',
  internal_approval: 'Internal approval',
  price_too_high: 'Price too high',
  product_fit: 'Product fit',
  product_quality: 'Product quality',
  alternative: 'Alternative',
  timing: 'Timing',
  other: 'Other',
};

const INTENT_LABELS = {
  active: 'Actively looking',
  planned: 'Planned',
  maybe: 'Maybe',
  no: 'No',
};

const LEVER_LABELS = {
  lower_price: 'Lower price',
  more_data: 'More data',
  specific_features: 'Specific features',
  flexible_billing: 'Flexible billing',
  consultation: 'Consultation',
  integrations: 'Integrations',
  longer_trial: 'Longer trial',
};

const PMF_LABELS = {
  very_disappointed: 'Very disappointed',
  somewhat_disappointed: 'Somewhat disappointed',
  not_disappointed: 'Not disappointed',
  never_used: 'Never used',
};

const UPGRADE_LABELS = {
  yes: 'Yes',
  no: 'No',
};

const USECASE_LABELS = {
  cell_sourcing: 'Cell sourcing',
  benchmarking: 'Benchmarking',
  product_development: 'Product development',
  research: 'Research',
  other: 'Other',
};

const TIMEFRAME_LABELS = {
  lt_1_month: '< 1 month',
  '1_3_months': '1–3 months',
  '3_6_months': '3–6 months',
  '6_12_months': '6–12 months',
  gt_12_months: '> 12 months',
  unsure: 'Not sure',
};

const MISSING_FEATURE_LABELS = {
  more_cells: 'More cells',
  cycle_life: 'Cycle-life data',
  cost_data: 'Cost data',
  thermal: 'Thermal',
  safety: 'Safety',
  api_access: 'API access',
  custom_export: 'Custom export',
  other: 'Other',
};

function getReasonBadgeClass(reason) {
  if (!reason) return 'admin-badge--basic';
  if (reason === 'price_too_high' || reason === 'budget') return 'admin-badge--warning';
  if (reason === 'product_quality' || reason === 'alternative') return 'admin-badge--error';
  if (reason === 'product_fit') return 'admin-badge--info';
  return 'admin-badge--basic';
}

function getPmfBadgeClass(pmf) {
  if (pmf === 'very_disappointed') return 'admin-badge--success';
  if (pmf === 'somewhat_disappointed') return 'admin-badge--info';
  if (pmf === 'not_disappointed') return 'admin-badge--warning';
  if (pmf === 'never_used') return 'admin-badge--error';
  return 'admin-badge--basic';
}

export function AdminConversionFeedbackTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(new Set());
  const [filters, setFilters] = useState({
    reason: '',
    intent: '',
    upgrade_intent: '',
    pmf_score: '',
    contact_requested: false,
    date_from: '',
    date_to: '',
  });
  const limit = 50;

  const queryParams = useMemo(() => ({
    limit,
    offset,
    ...(filters.reason && { reason: filters.reason }),
    ...(filters.intent && { intent: filters.intent }),
    ...(filters.upgrade_intent && { upgrade_intent: filters.upgrade_intent }),
    ...(filters.pmf_score && { pmf_score: filters.pmf_score }),
    ...(filters.contact_requested && { contact_requested: true }),
    ...(filters.date_from && { date_from: filters.date_from }),
    ...(filters.date_to && { date_to: filters.date_to }),
  }), [filters, offset]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getConversionFeedback(queryParams);
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load feedback');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [queryParams]);

  const toggleExpand = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const handleFilterChange = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
    setOffset(0);
  };

  const csvUrl = getConversionFeedbackCsvUrl({
    ...(filters.reason && { reason: filters.reason }),
    ...(filters.intent && { intent: filters.intent }),
    ...(filters.upgrade_intent && { upgrade_intent: filters.upgrade_intent }),
    ...(filters.pmf_score && { pmf_score: filters.pmf_score }),
    ...(filters.contact_requested && { contact_requested: true }),
    ...(filters.date_from && { date_from: filters.date_from }),
    ...(filters.date_to && { date_to: filters.date_to }),
  });

  return (
    <div className="admin-tab">
      <div className="admin-tab-header">
        <h2>Conversion Feedback</h2>
        <p className="admin-tab-description">
          Responses from demo users about why they did not upgrade to Pro.
          Submitted via the day-14 nurture email. {total} total {total === 1 ? 'entry' : 'entries'}.
        </p>
      </div>

      <div className="admin-filters-row" style={filtersRowStyle}>
        <select
          value={filters.pmf_score}
          onChange={e => handleFilterChange('pmf_score', e.target.value)}
          className="admin-select"
          style={filterControlStyle}
        >
          <option value="">All PMF</option>
          {Object.entries(PMF_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          value={filters.upgrade_intent}
          onChange={e => handleFilterChange('upgrade_intent', e.target.value)}
          className="admin-select"
          style={filterControlStyle}
        >
          <option value="">All upgrade intent</option>
          <option value="yes">Yes — considering</option>
          <option value="no">No — not now</option>
        </select>
        <select
          value={filters.reason}
          onChange={e => handleFilterChange('reason', e.target.value)}
          className="admin-select"
          style={filterControlStyle}
        >
          <option value="">All reasons</option>
          {Object.entries(REASON_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          value={filters.intent}
          onChange={e => handleFilterChange('intent', e.target.value)}
          className="admin-select"
          style={filterControlStyle}
        >
          <option value="">All intents (V1)</option>
          {Object.entries(INTENT_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <label style={dateLabelStyle}>
          <span style={dateLabelTextStyle}>From</span>
          <input
            type="date"
            value={filters.date_from}
            onInput={e => handleFilterChange('date_from', e.target.value)}
            className="admin-input"
            style={dateInputStyle}
          />
        </label>
        <label style={dateLabelStyle}>
          <span style={dateLabelTextStyle}>To</span>
          <input
            type="date"
            value={filters.date_to}
            onInput={e => handleFilterChange('date_to', e.target.value)}
            className="admin-input"
            style={dateInputStyle}
          />
        </label>
        <label style={checkboxLabelStyle}>
          <input
            type="checkbox"
            checked={filters.contact_requested}
            onChange={e => handleFilterChange('contact_requested', e.target.checked)}
            style={{ accentColor: '#ea5b0c' }}
          />
          Contact requested
        </label>
        <a
          href={csvUrl}
          target="_blank"
          rel="noreferrer"
          style={exportButtonStyle}
        >
          Export CSV
        </a>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Submitted</th>
              <th>Email</th>
              <th>Company</th>
              <th>PMF</th>
              <th>Like</th>
              <th>Upgrade</th>
              <th>Reason / Use case</th>
              <th>Contact</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                  No feedback submissions yet.
                </td>
              </tr>
            )}
            {items.map(item => {
              const isExpanded = expanded.has(item.id);
              const isV2 = !!item.pmf_score || !!item.upgrade_intent;
              const summaryLabel = item.upgrade_intent === 'yes'
                ? (USECASE_LABELS[item.primary_usecase] || item.primary_usecase || '—')
                : (
                    // V2b prefers free-text main reason; fall back to legacy enum
                    item.no_path_main_reason
                      ? (item.no_path_main_reason.length > 40
                          ? item.no_path_main_reason.slice(0, 37) + '…'
                          : item.no_path_main_reason)
                      : (REASON_LABELS[item.primary_reason] || item.primary_reason || '—')
                  );
              const summaryClass = item.upgrade_intent === 'yes'
                ? 'admin-badge--info'
                : (item.no_path_main_reason ? 'admin-badge--basic' : getReasonBadgeClass(item.primary_reason));
              return (
                <>
                  <tr key={item.id}>
                    <td>{formatDateTime(item.submitted_at)}</td>
                    <td>{item.user_email}</td>
                    <td>{item.user_company || '—'}</td>
                    <td>
                      {item.pmf_score ? (
                        <span className={`admin-badge ${getPmfBadgeClass(item.pmf_score)}`}>
                          {PMF_LABELS[item.pmf_score] || item.pmf_score}
                        </span>
                      ) : (
                        <span style={{ color: '#666', fontSize: 12 }}>v1 legacy</span>
                      )}
                    </td>
                    <td>{item.like_rating != null ? `${item.like_rating}/10` : (item.value_rating != null ? `${item.value_rating}/5` : '—')}</td>
                    <td>
                      {item.upgrade_intent
                        ? UPGRADE_LABELS[item.upgrade_intent]
                        : (INTENT_LABELS[item.purchase_intent] || '—')}
                    </td>
                    <td>
                      <span className={`admin-badge ${summaryClass}`}>{summaryLabel}</span>
                    </td>
                    <td>
                      {item.contact_requested ? (
                        <span className="admin-badge admin-badge--warning" style={{ background: '#ea5b0c', color: '#fff' }}>
                          Yes
                        </span>
                      ) : (
                        <span style={{ color: '#666' }}>—</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="admin-button admin-button--ghost"
                        onClick={() => toggleExpand(item.id)}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? '−' : '+'}
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${item.id}-details`}>
                      <td colSpan={9} style={{ background: '#141414', padding: 16 }}>
                        <DetailPanel item={item} isV2={isV2} />
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      )}

      {total > limit && (
        <div className="admin-pagination" style={{ marginTop: 12 }}>
          <button
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
            className="admin-button"
          >
            ← Prev
          </button>
          <span style={{ margin: '0 12px' }}>
            {offset + 1}–{Math.min(offset + limit, total)} of {total}
          </span>
          <button
            disabled={offset + limit >= total}
            onClick={() => setOffset(offset + limit)}
            className="admin-button"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function DetailPanel({ item, isV2 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {isV2 && (
        <>
          <SectionBlock title="Affect & intent">
            <Field label="PMF" value={PMF_LABELS[item.pmf_score] || item.pmf_score} />
            <Field label="Like rating" value={item.like_rating != null ? `${item.like_rating}/10` : null} />
            <Field label="Upgrade intent" value={UPGRADE_LABELS[item.upgrade_intent] || item.upgrade_intent} />
          </SectionBlock>

          {item.upgrade_intent === 'yes' && (
            <SectionBlock title="Yes-path — sales-relevant">
              <Field
                label="Use case"
                value={
                  item.primary_usecase === 'other' && item.primary_usecase_other
                    ? `Other — ${item.primary_usecase_other}`
                    : (USECASE_LABELS[item.primary_usecase] || item.primary_usecase)
                }
              />
              <Field
                label="Approval timeframe"
                value={TIMEFRAME_LABELS[item.approval_timeframe] || item.approval_timeframe}
              />
              <Field label="Internal approval help" value={item.internal_approval_help} fullWidth />
              <Field label="Liked most" value={item.liked_most} fullWidth />
            </SectionBlock>
          )}

          {item.upgrade_intent === 'no' && (
            <SectionBlock title="No-path — improvement signal">
              <Field
                label="Main reason (free text)"
                value={item.no_path_main_reason}
                fullWidth
              />
              {(item.primary_reason || item.primary_reason_other) && (
                <Field
                  label="Primary reason (legacy enum)"
                  value={
                    item.primary_reason === 'other' && item.primary_reason_other
                      ? `Other — ${item.primary_reason_other}`
                      : (REASON_LABELS[item.primary_reason] || item.primary_reason)
                  }
                />
              )}
              <Field label="Improvement feedback" value={item.improvement_feedback} fullWidth />
              <Field
                label="Missing features"
                value={
                  (item.missing_features || []).length
                    ? (item.missing_features || [])
                        .map(f =>
                          f === 'other' && item.missing_features_other
                            ? `Other — ${item.missing_features_other}`
                            : (MISSING_FEATURE_LABELS[f] || f)
                        )
                        .join(', ')
                    : null
                }
                fullWidth
              />
              <Field label="Missing cells" value={item.missing_cells} fullWidth />
            </SectionBlock>
          )}

          <SectionBlock title="Contact">
            <Field label="User opted in" value={item.contact_requested ? 'Yes' : 'No'} />
            <Field
              label="Sales notified at"
              value={item.contact_email_sent_at ? formatDateTime(item.contact_email_sent_at) : '—'}
            />
          </SectionBlock>
        </>
      )}

      {/* V1 legacy block — only render if any V1-only field is present */}
      {(item.value_rating != null ||
        item.value_feedback ||
        (item.conversion_levers || []).length ||
        item.purchase_intent ||
        item.free_feedback) && (
        <SectionBlock title={isV2 ? 'Legacy V1 fields' : 'V1 fields'}>
          <Field
            label="Rating feedback"
            value={
              item.value_rating != null
                ? `${item.value_rating}/5 — ${item.value_feedback || '—'}`
                : null
            }
            fullWidth
          />
          <Field
            label="Conversion levers"
            value={
              (item.conversion_levers || []).length
                ? (item.conversion_levers || []).map(l => LEVER_LABELS[l] || l).join(', ')
                : null
            }
            fullWidth
          />
          <Field label="Features mentioned" value={item.conversion_levers_features} fullWidth />
          <Field label="Free feedback (V1)" value={item.free_feedback} fullWidth />
        </SectionBlock>
      )}
    </div>
  );
}

function SectionBlock({ title, children }) {
  return (
    <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#ea5b0c', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
        {title}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, fullWidth }) {
  return (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : 'auto' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, color: '#e0e0e0', whiteSpace: 'pre-wrap' }}>
        {value || <span style={{ color: '#555' }}>—</span>}
      </div>
    </div>
  );
}

// .admin-input has width:100% which collapses date inputs onto their own
// rows in a flex-wrap layout. The styles below constrain widths so the
// filter row stays single-line on desktop and wraps gracefully on narrow
// screens.

const filtersRowStyle = {
  display: 'flex',
  gap: 12,
  flexWrap: 'wrap',
  alignItems: 'center',
  margin: '16px 0',
};

const filterControlStyle = {
  width: 'auto',
  minWidth: 160,
  flexShrink: 0,
};

const dateLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  color: '#9ca3af',
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const dateLabelTextStyle = { whiteSpace: 'nowrap' };

const dateInputStyle = {
  width: 160,
  padding: '8px 10px',
  colorScheme: 'dark',
};

const checkboxLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: '#e0e0e0',
  fontSize: 13,
  padding: '8px 12px',
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: 8,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const exportButtonStyle = {
  marginLeft: 'auto',
  padding: '10px 18px',
  background: 'rgba(234, 91, 12, 0.12)',
  border: '1px solid rgba(234, 91, 12, 0.4)',
  borderRadius: 8,
  color: '#ea5b0c',
  fontSize: 13,
  fontWeight: 600,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};
